if (!customElements.get('collection-filters')) {
  class CollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.drawer = null;
      this.filterButton = null;
      this.abortController = null;
      this.desktopQuery = window.matchMedia('(min-width: 1024px)');
      this.handleDesktopChange = this.handleDesktopChange.bind(this);
      this.handleDrawerOpen = this.handleDrawerOpen.bind(this);
      this.handleDrawerClose = this.handleDrawerClose.bind(this);
      this.handleDelegatedChange = this.handleDelegatedChange.bind(this);
      this.handleDelegatedClick = this.handleDelegatedClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this._drawerOpenUnsub = null;
      this._drawerCloseUnsub = null;
      this._stuckRafId = null;
    }

    connectedCallback() {
      this.drawer = this.querySelector('theme-drawer[data-filter-drawer]') || document.querySelector('[data-filter-drawer]');
      this.filterButton = this.querySelector('[data-filter-button]');

      this.initFilterDrawer();
      this.initDelegatedEvents();
      this.initFilterListHeights();

      if (this.drawer) {
        this.updateDrawerSide();
        this.desktopQuery.addEventListener('change', this.handleDesktopChange);
      }

      if (window.theme?.pubsub && this.drawer?.id) {
        const events = theme.pubsub.events;
        this._drawerOpenUnsub = theme.pubsub.subscribe(events.DRAWER_OPEN, ({ id }) => {
          if (id === this.drawer.id) this.handleDrawerOpen();
        });
        this._drawerCloseUnsub = theme.pubsub.subscribe(events.DRAWER_CLOSE, ({ id }) => {
          if (id === this.drawer.id) this.handleDrawerClose();
        });
      }

      this.onPopState = () => this.fetchAndUpdatePage(window.location.href, 'filter', { fromPopState: true });
      window.addEventListener('popstate', this.onPopState);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      this.updateStuckState();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.removeEventListener('popstate', this.onPopState);
      this.desktopQuery.removeEventListener('change', this.handleDesktopChange);
      this._drawerOpenUnsub?.();
      this._drawerCloseUnsub?.();
      document.removeEventListener('change', this.handleDelegatedChange);
      document.removeEventListener('click', this.handleDelegatedClick);
      window.removeEventListener('scroll', this.handleScroll);
      if (this._stuckRafId) cancelAnimationFrame(this._stuckRafId);
    }

    handleScroll() {
      if (this._stuckRafId) return;
      this._stuckRafId = requestAnimationFrame(() => {
        this.updateStuckState();
        this._stuckRafId = null;
      });
    }

    updateStuckState() {
      const wrap = this.closest('.shopify-section');
      if (!wrap || wrap.querySelector(':scope > collection-filters') !== this) return;

      const resolvedTop = parseFloat(getComputedStyle(wrap).top);
      const stuck = !Number.isNaN(resolvedTop) && wrap.getBoundingClientRect().top <= resolvedTop + 1;

      if (stuck !== this.classList.contains('is-stuck')) {
        this.classList.toggle('is-stuck', stuck);
      }

      const height = `${wrap.getBoundingClientRect().height}px`;
      if (document.documentElement.style.getPropertyValue('--collection-filters-bar-height') !== height) {
        document.documentElement.style.setProperty('--collection-filters-bar-height', height);
      }
    }

    updateDrawerSide() {
      if (!this.drawer) return;
      this.drawer.dataset.side = this.desktopQuery.matches ? 'left' : 'right';
    }

    handleDesktopChange() {
      const wasOpen = this.isFilterDrawerOpen();
      if (wasOpen) this.drawer.close();
      this.updateDrawerSide();
      this.initFilterListHeights();
      this.updateStuckState();
    }

    handleDrawerOpen() {
      this.filterButton?.setAttribute('aria-expanded', 'true');
      this.updateFilterButtonLabel(true);
      this.initFilterListHeights();
      requestAnimationFrame(() => this.updateStuckState());
    }

    handleDrawerClose() {
      this.filterButton?.setAttribute('aria-expanded', 'false');
      this.updateFilterButtonLabel(false);
      this.filterButton?.focus();
      requestAnimationFrame(() => this.updateStuckState());
    }

    updateFilterButtonLabel(isOpen) {
      const textEl = this.filterButton?.querySelector('[data-filter-button-text]');
      if (!textEl) return;
      const label = isOpen ? textEl.dataset.labelClose : textEl.dataset.labelOpen;
      if (label) textEl.textContent = label;
    }

    initDelegatedEvents() {
      document.addEventListener('change', this.handleDelegatedChange);
      document.addEventListener('click', this.handleDelegatedClick);
    }

    handleDelegatedChange(e) {
      if (e.target.matches('[data-filter-input]')) {
        this.handleFilterChange(e);
      } else if (e.target.matches('[data-sort-radio]')) {
        this.handleSortRadioChange(e);
      }
    }

    handleDelegatedClick(e) {
      const chipRemove = e.target.closest('.collection-active-filters__chip-remove');
      if (chipRemove) {
        e.preventDefault();
        this.fetchAndUpdatePage(chipRemove.href);
        return;
      }

      const clearAll = e.target.closest('.collection-active-filters__clear');
      if (clearAll) {
        e.preventDefault();
        this.fetchAndUpdatePage(clearAll.href);
        return;
      }

      const applyBtn = e.target.closest('[data-filter-apply]');
      if (applyBtn) {
        this.drawer?.close();
        return;
      }

      const moreToggle = e.target.closest('[data-filter-more]');
      if (moreToggle) {
        e.preventDefault();
        this.toggleFilterList(moreToggle);
        return;
      }

      const sortOption = e.target.closest('[data-sort-option]');
      if (sortOption) {
        e.preventDefault();
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortOption.dataset.sortOption);
        this.querySelector('theme-dropdown[data-mode="listbox"]')?.close();
        this.fetchAndUpdatePage(url.toString(), 'sort');
      }
    }

    initFilterDrawer() {
      if (!this.filterButton || !this.drawer) return;

      this.filterButton.addEventListener('click', () => {
        this.isFilterDrawerOpen() ? this.drawer.close() : this.drawer.open();
      });
      this.drawer.querySelector('[data-filter-drawer-close]')?.addEventListener('click', () => this.drawer.close());
    }

    isFilterDrawerOpen() {
      return this.drawer?.hasAttribute('data-open') ?? false;
    }

    handleSortRadioChange(e) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', e.target.value);
      this.fetchAndUpdatePage(url.toString(), 'sort');
    }

    async handleFilterChange(e) {
      const input = e.target;
      const isChecked = input.checked;
      let targetUrl;

      if (isChecked) {
        targetUrl = input.dataset.filterUrl;
      } else if (input.dataset.filterType === 'price_range') {
        const url = new URL(window.location.href);
        url.searchParams.delete(input.dataset.filterParamGte);
        url.searchParams.delete(input.dataset.filterParamLte);
        targetUrl = url.toString();
      } else {
        targetUrl = input.dataset.filterRemoveUrl;
      }

      if (!targetUrl) return;

      await this.fetchAndUpdatePage(targetUrl);
    }

    async fetchAndUpdatePage(targetUrl, eventType = 'filter', { fromPopState = false } = {}) {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      const drawerWasOpen = this.isFilterDrawerOpen();
      this.setLoading(true);

      const cacheKey = theme.cache?.scopedKey(`cfilters:${targetUrl}`);

      try {
        const cachedHtml = theme.cache?.get(cacheKey);
        const html = cachedHtml || await fetch(targetUrl, { signal }).then((response) => response.text());

        if (!cachedHtml && html.length <= (theme.cache?.MAX_CACHEABLE_LENGTH ?? Infinity)) theme.cache?.set(cacheKey, html, theme.cache.TTL_FRESH);

        const doc = new DOMParser().parseFromString(html, 'text/html');

        this.updateDrawerContent(doc);
        this.updateActiveFilters(doc);
        this.updateProductGrid(doc);
        this.updatePagination(doc);
        this.updateSortDropdown(doc);
        this.updateApplyButtonCount(doc);
        this.updateResultsCount(doc);

        if (!fromPopState) window.history.pushState({}, '', targetUrl);

        if (drawerWasOpen) {
          this.drawer?.refreshFocus();
        }

        if (!fromPopState) this.publishFilterEvent(eventType, doc, targetUrl);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Filter/sort update error:', error);
        window.location.href = targetUrl;
      } finally {
        if (!signal.aborted) {
          this.abortController = null;
          this.setLoading(false);
        }
      }
    }

    updateDrawerContent(doc) {
      const newContent = doc.querySelector('[data-filter-drawer-content]');
      const currentContent = this.drawer?.querySelector('[data-filter-drawer-content]');
      if (!newContent || !currentContent) return;

      const expandedListIds = [...currentContent.querySelectorAll('[data-filter-list][data-collapsed="false"]')]
        .map((list) => list.id)
        .filter(Boolean);

      currentContent.innerHTML = newContent.innerHTML;

      this.restoreExpandedFilterLists(currentContent, expandedListIds);
      this.measureExpandedFilterLists(currentContent);
    }

    restoreExpandedFilterLists(content, expandedListIds) {
      expandedListIds.forEach((id) => {
        const list = content.querySelector(`#${id}`);
        if (!list || list.dataset.collapsed === 'false') return;

        const button = content.querySelector(`[data-filter-more][aria-controls="${id}"]`);
        if (button) {
          this.toggleFilterList(button);
        } else {
          list.dataset.collapsed = 'false';
        }
      });
    }

    toggleFilterList(button) {
      const list = document.getElementById(button.getAttribute('aria-controls'));
      if (!list) return;

      const expand = list.dataset.collapsed === 'true';
      list.dataset.collapsed = expand ? 'false' : 'true';
      button.setAttribute('aria-expanded', expand ? 'true' : 'false');

      const moreLabel = button.querySelector('[data-more-label]');
      const lessLabel = button.querySelector('[data-less-label]');
      if (moreLabel) moreLabel.hidden = expand;
      if (lessLabel) lessLabel.hidden = !expand;

      if (expand && list.dataset.scrollable === 'true') {
        this.measureFilterListHeight(list);
      }
    }

    measureExpandedFilterLists(root) {
      root.querySelectorAll('[data-filter-list][data-scrollable="true"][data-collapsed="false"]').forEach((list) => {
        this.measureFilterListHeight(list);
      });
    }

    initFilterListHeights() {
      if (!this.drawer) return;
      this.measureExpandedFilterLists(this.drawer);
    }

    measureFilterListHeight(list) {
      const options = list.querySelectorAll(':scope > .filter-options__option');
      const eleventh = options[10];
      if (!eleventh) return;

      const listTop = list.getBoundingClientRect().top;
      const height = eleventh.getBoundingClientRect().top - listTop;
      if (height > 0) list.style.setProperty('--filter-list-max-height', `${height}px`);
    }

    updateActiveFilters(doc) {
      const newFilters = doc.querySelector('[data-active-filters]');
      const currentFilters = this.drawer?.querySelector('[data-active-filters]');

      if (newFilters) {
        if (currentFilters) {
          currentFilters.replaceWith(newFilters);
        } else {
          const content = this.drawer?.querySelector('[data-filter-drawer-content]');
          if (content) content.insertAdjacentElement('afterbegin', newFilters);
        }
      } else if (currentFilters) {
        currentFilters.remove();
      }
    }

    getGridSelector() {
      return this.dataset.gridTarget || '#product-grid';
    }

    getPaginationSelector() {
      return this.dataset.paginationTarget || '.collection-grid__pagination';
    }

    updateProductGrid(doc) {
      const gridSelector = this.getGridSelector();
      const newGrid = doc.querySelector(gridSelector);
      const currentGrid = document.querySelector(gridSelector);
      if (!newGrid || !currentGrid) return;

      currentGrid.innerHTML = newGrid.innerHTML;
    }

    updatePagination(doc) {
      const paginationSelector = this.getPaginationSelector();
      const newPagination = doc.querySelector(paginationSelector);
      const currentPagination = document.querySelector(paginationSelector);
      if (newPagination && currentPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      }
    }

    updateSortDropdown(doc) {
      const newValue = doc.querySelector('[data-sort-value]');
      const currentValue = this.querySelector('[data-sort-value]');
      if (newValue && currentValue) {
        currentValue.textContent = newValue.textContent;
      }

      const newMenu = doc.querySelector('[data-sort-menu]');
      const currentMenu = this.querySelector('[data-sort-menu]');
      if (newMenu && currentMenu) {
        currentMenu.innerHTML = newMenu.innerHTML;
      }
    }

    updateApplyButtonCount(doc) {
      const newCount = doc.querySelector('[data-filter-apply-count]');
      const countEl = this.drawer?.querySelector('[data-filter-apply-count]');
      if (!newCount || !countEl) return;

      countEl.textContent = newCount.textContent.trim();
    }

    updateResultsCount(doc) {
      const newCount = doc.querySelector('[data-results-count]');
      const currentCount = document.querySelector('[data-results-count]');
      if (newCount && currentCount) currentCount.innerHTML = newCount.innerHTML;
    }

    publishFilterEvent(eventType, doc, url) {
      if (!window.theme?.pubsub) return;
      const events = theme.pubsub.events;

      if (eventType === 'sort') {
        theme.pubsub.publish(events.SORT_CHANGED, { url });
        return;
      }

      const activeCount = doc.querySelectorAll('[data-active-filters] .collection-active-filters__chip').length;
      if (activeCount > 0) {
        theme.pubsub.publish(events.FILTER_APPLIED, { url, activeCount });
      } else {
        theme.pubsub.publish(events.FILTER_CLEARED, { url });
      }
    }

    setLoading(show) {
      const drawerContent = this.drawer?.querySelector('[data-filter-drawer-content]');
      const grid = document.querySelector(this.getGridSelector());

      if (show) {
        if (drawerContent) {
          drawerContent.style.opacity = '0.5';
          drawerContent.style.pointerEvents = 'none';
        }

        if (grid) this.renderGridSkeleton(grid);
      } else {
        if (drawerContent) {
          drawerContent.style.opacity = '';
          drawerContent.style.pointerEvents = '';
        }

        if (grid) {
          grid.removeAttribute('aria-busy');
          grid.removeAttribute('aria-live');
        }
      }
    }

    renderGridSkeleton(grid) {
      const itemSelector = grid.classList.contains('grill') ? '.grill__item' : '.grid__item';
      const itemTag = itemSelector === '.grill__item' ? 'grill__item' : 'grid__item';
      const count = grid.querySelectorAll(itemSelector).length || 8;

      grid.innerHTML = Array.from({ length: count }, () => `
        <li class="${itemTag}">
          <span class="skeleton skeleton--product-card" role="presentation" aria-hidden="true">
            <span class="skeleton skeleton--product-card__image"></span>
            <span class="skeleton skeleton--product-card__title"></span>
            <span class="skeleton skeleton--product-card__price"></span>
          </span>
        </li>
      `).join('');

      grid.setAttribute('aria-busy', 'true');
      grid.setAttribute('aria-live', 'polite');
    }
  }

  customElements.define('collection-filters', CollectionFilters);
}

if (!customElements.get('price-range-slider')) {
  class PriceRangeSlider extends HTMLElement {
    constructor() {
      super();
      this.minInput = this.querySelector('.price-range-slider__input--min');
      this.maxInput = this.querySelector('.price-range-slider__input--max');
      this.minLabel = this.querySelector('.price-range-slider__label--min');
      this.maxLabel = this.querySelector('.price-range-slider__label--max');
      this.trackFill = this.querySelector('.price-range-slider__track-fill');

      this.debounceTimer = null;
      this.handleInputChange = this.handleInputChange.bind(this);
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      this.min = parseInt(this.dataset.min) || 0;
      this.max = parseInt(this.dataset.max) || 100;
      this.currentMin = parseInt(this.dataset.currentMin) || this.min;
      this.currentMax = parseInt(this.dataset.currentMax) || this.max;
      this.paramName = this.dataset.paramName || 'filter.p.price';
      this.currency = this.dataset.currency || '{{ amount }}';

      this.updateTrackFill();

      this.minInput?.addEventListener('input', this.handleInputChange);
      this.maxInput?.addEventListener('input', this.handleInputChange);
      this.minInput?.addEventListener('change', this.handleChange);
      this.maxInput?.addEventListener('change', this.handleChange);
    }

    disconnectedCallback() {
      this.minInput?.removeEventListener('input', this.handleInputChange);
      this.maxInput?.removeEventListener('input', this.handleInputChange);
      this.minInput?.removeEventListener('change', this.handleChange);
      this.maxInput?.removeEventListener('change', this.handleChange);

      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }
    }

    handleInputChange() {
      let minValue = parseInt(this.minInput.value);
      let maxValue = parseInt(this.maxInput.value);

      if (minValue > maxValue) {
        minValue = maxValue;
        this.minInput.value = minValue;
      }

      if (maxValue < minValue) {
        maxValue = minValue;
        this.maxInput.value = maxValue;
      }

      this.currentMin = minValue;
      this.currentMax = maxValue;

      this.updateLabels();
      this.updateTrackFill();
    }

    handleChange() {
      if (this.debounceTimer) {
        clearTimeout(this.debounceTimer);
      }

      this.debounceTimer = setTimeout(() => {
        this.applyFilter();
      }, 300);
    }

    applyFilter() {
      const url = new URL(window.location.href);
      const minValue = parseInt(this.minInput.value);
      const maxValue = parseInt(this.maxInput.value);

      if (minValue === this.min && maxValue === this.max) {
        url.searchParams.delete(`${this.paramName}.gte`);
        url.searchParams.delete(`${this.paramName}.lte`);
      } else {
        if (minValue > this.min) {
          url.searchParams.set(`${this.paramName}.gte`, (minValue / 100).toFixed(2));
        } else {
          url.searchParams.delete(`${this.paramName}.gte`);
        }

        if (maxValue < this.max) {
          url.searchParams.set(`${this.paramName}.lte`, (maxValue / 100).toFixed(2));
        } else {
          url.searchParams.delete(`${this.paramName}.lte`);
        }
      }

      const collectionFilters = this.closest('collection-filters') || document.querySelector('collection-filters');
      if (collectionFilters) {
        collectionFilters.fetchAndUpdatePage(url.toString());
      }
    }

    updateLabels() {
      if (this.minLabel) {
        this.minLabel.textContent = this.formatMoney(this.currentMin);
      }
      if (this.maxLabel) {
        this.maxLabel.textContent = this.formatMoney(this.currentMax);
      }
    }

    updateTrackFill() {
      if (!this.trackFill) return;

      const range = this.max - this.min;
      if (range === 0) return;
      const minPercent = ((this.currentMin - this.min) / range) * 100;
      const maxPercent = ((this.currentMax - this.min) / range) * 100;

      this.trackFill.style.left = `${minPercent}%`;
      this.trackFill.style.width = `${maxPercent - minPercent}%`;
    }

    formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      const amountNoDecimals = Math.floor(cents / 100);

      const amountWithComma = amount.replace('.', ',').replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
      const amountNoDecimalsWithComma = amountNoDecimals.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');

      const amountWithApostrophe = amount.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1'");

      return this.currency
        .replace('{{amount_with_comma_separator}}', amountWithComma)
        .replace('{{ amount_with_comma_separator }}', amountWithComma)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithComma)
        .replace('{{ amount_no_decimals_with_comma_separator }}', amountNoDecimalsWithComma)
        .replace('{{amount_with_apostrophe_separator}}', amountWithApostrophe)
        .replace('{{ amount_with_apostrophe_separator }}', amountWithApostrophe)
        .replace('{{amount}}', amount)
        .replace('{{ amount }}', amount)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{ amount_no_decimals }}', amountNoDecimals);
    }
  }

  customElements.define('price-range-slider', PriceRangeSlider);
}
